import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getDefaultMobileWidth } from '../../custom-hooks/screen-detector';

import { MText } from '../MText';
import { MBox } from '../MBox';

import {
  getFourSideStringFormat,
  sanitizeThemeStyleByNumber,
} from '../../helpers/style';

const StyledSelectCon = styled(MBox)`
  position: relative;
  overflow: hidden;
  padding: 0px;
  min-height: ${props => sanitizeThemeStyleByNumber(props.height)};
  min-width: ${props => sanitizeThemeStyleByNumber(props.width)};

  background-position: calc(100% - 8px) center !important;
  background-color: ${props => props.$backgroundColor};
  background-image: url("data:image/svg+xml,<svg height='10px' width='10px' viewBox='0 0 16 16' fill='${props =>
    props.$color.replace(
      '#',
      '%23',
    )}' xmlns='http://www.w3.org/2000/svg'><path d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/></svg>");
  background-repeat: no-repeat;
  ${props => (props.$buttonStyle === 'text' ? 'background: none' : null)};

  & .mr-input-select-overlay {
    position: static;
    appearance: none;
    background: transparent !important;
    line-height: 1.3;
    font-family: ${props => props.$fontFamily};
    border: 0px solid transparent;
    border-radius: ${props => props.$borderRadius}px;
    color: ${props => props.$color};
    font-size: ${props => props.$fontSize}px;
    font-style: ${props => props.$fontStyle};
    font-weight: ${props => props.$fontWeight};
    text-decoration: ${props => props.$textDecoration};
    text-align: ${props => props.$textAlignment};
    padding: ${props => props.$padding};
    margin: 0px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: ${props => props.$textAlignment};

    .value {
      width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      display: inline-block;
      white-space: nowrap;
      text-align: ${props => props.$textAlignment};
    }

    @media (max-width: ${getDefaultMobileWidth()}px) {
      font-size: ${props => props.$mobileFontSize}px;
    }
  } 

`;

const StyledLabel = styled(MText)`
  position: absolute;
  left: 10px;
  top: 5px;
`;

const MSelectCon = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  -ms-appearance: none;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  margin: 0px;
  width: 100%;
  height: 100%;

  :hover {
    outline: none;
  }
  :focus {
    outline: none;
    box-shadow: none;
    border-color: transparent;
  }
  :focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

export const MSelect = ({
  options,
  onChange,
  value,
  style,
  fontSize,
  mobileFontSize,
  color,
  borderColor,
  backgroundColor,
  borderRadius,
  fontStyle,
  fontWeight,
  textDecoration,
  fontFamily,
  padding,
  margin,
  width,
  height,
  textAlignment,
  className,
  attributes = {},
}) => {
  const border =
    style !== 'text' ? `1px solid ${borderColor}` : '1px solid transparent';

  return (
    <StyledSelectCon
      height={height}
      width={width}
      margin={margin}
      border={border}
      $buttonStyle={style}
      $color={color}
      $fontSize={fontSize}
      $mobileFontSize={mobileFontSize}
      $fontStyle={fontStyle}
      $fontWeight={fontWeight}
      $textDecoration={textDecoration}
      $textAlignment={textAlignment}
      $fontFamily={fontFamily}
      $padding={padding}
      $backgroundColor={style === 'filled' ? backgroundColor : 'transparent'}
      borderRadius={getFourSideStringFormat(borderRadius)}
      className={`${className} mr-input-select-wrapper`}
    >
      <MSelectCon
        className="mr-input-select"
        onChange={onChange}
        value={value}
        data-mr-input
        {...attributes}
      >
        {options.map((val, i) => (
          <option
            key={`option${i * 1}`}
            className="mr-input-option"
            value={val}
          >
            {val}
          </option>
        ))}
      </MSelectCon>
      <div className="mr-input-select-overlay">
        <div className="value">{value}</div>
      </div>
    </StyledSelectCon>
  );
};

MSelect.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  value: PropTypes.string,
  style: PropTypes.string.isRequired,
  fontSize: PropTypes.string.isRequired,
  mobileFontSize: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  borderRadius: PropTypes.string.isRequired,
  fontStyle: PropTypes.string.isRequired,
  fontWeight: PropTypes.string.isRequired,
  textDecoration: PropTypes.string.isRequired,
  fontFamily: PropTypes.string.isRequired,
  padding: PropTypes.string.isRequired,
  margin: PropTypes.string.isRequired,
  width: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  textAlignment: PropTypes.string.isRequired,
  className: PropTypes.string,
  attributes: PropTypes.object,
};
